import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const FeaturedChallenge = ({ challenge, onParticipate }) => {
  const navigate = useNavigate();

  const getTimeRemaining = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diff = end - now;
    
    if (diff <= 0) return 'Ended';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m left`;
  };

  const getProgressPercentage = () => {
    return Math.min((challenge?.currentAmount / challenge?.goalAmount) * 100, 100);
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  return (
    <div className="bg-gradient-to-r from-primary/10 via-secondary/10 to-accent/10 border border-border rounded-lg overflow-hidden mb-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
        {/* Image Section */}
        <div className="relative h-64 lg:h-auto overflow-hidden">
          <Image
            src={challenge?.image}
            alt={challenge?.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          <div className="absolute top-4 left-4">
            <span className="px-3 py-1 bg-warning text-warning-foreground rounded-full text-sm font-medium flex items-center space-x-1">
              <Icon name="Star" size={14} />
              <span>Featured</span>
            </span>
          </div>
          <div className="absolute bottom-4 left-4 right-4">
            <div className="flex items-center justify-between text-white">
              <div className="flex items-center space-x-2">
                <Icon name="Users" size={16} />
                <span className="text-sm font-medium">
                  {challenge?.participantCount} participants
                </span>
              </div>
              <div className="flex items-center space-x-1 bg-black/30 backdrop-blur-sm px-2 py-1 rounded">
                <Icon name="Clock" size={14} />
                <span className="text-sm font-medium">
                  {getTimeRemaining(challenge?.endDate)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-6 lg:p-8 flex flex-col justify-center">
          <div className="mb-4">
            <div className="flex items-center space-x-2 mb-2">
              <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                {challenge?.category}
              </span>
              <span className="text-xs text-muted-foreground">
                Ends {new Date(challenge.endDate)?.toLocaleDateString()}
              </span>
            </div>
            <h2 className="text-2xl lg:text-3xl font-bold text-card-foreground mb-3">
              {challenge?.title}
            </h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              {challenge?.description}
            </p>
          </div>

          {/* Progress Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-2xl font-bold text-card-foreground">
                  {formatAmount(challenge?.currentAmount)}
                </p>
                <p className="text-sm text-muted-foreground">
                  of {formatAmount(challenge?.goalAmount)} goal
                </p>
              </div>
              <div className="text-right">
                <p className="text-lg font-semibold text-primary">
                  {Math.round(getProgressPercentage())}%
                </p>
                <p className="text-sm text-muted-foreground">complete</p>
              </div>
            </div>
            <div className="w-full bg-muted rounded-full h-3">
              <div 
                className="bg-gradient-to-r from-primary to-secondary h-3 rounded-full transition-all duration-1000"
                style={{ width: `${getProgressPercentage()}%` }}
              />
            </div>
          </div>

          {/* Top Contributors */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-card-foreground mb-3">
              Top Contributors
            </h4>
            <div className="flex items-center space-x-3">
              {challenge?.topContributors?.slice(0, 5)?.map((contributor, index) => (
                <div key={contributor?.id} className="flex flex-col items-center">
                  <div className="relative">
                    <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                      {contributor?.name?.charAt(0)}
                    </div>
                    {index === 0 && (
                      <Icon 
                        name="Crown" 
                        size={12} 
                        className="absolute -top-1 -right-1 text-warning" 
                      />
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground mt-1">
                    {contributor?.name?.split(' ')?.[0]}
                  </span>
                </div>
              ))}
              {challenge?.participantCount > 5 && (
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 bg-muted text-muted-foreground rounded-full flex items-center justify-center text-xs">
                    +{challenge?.participantCount - 5}
                  </div>
                  <span className="text-xs text-muted-foreground mt-1">more</span>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            <Button
              onClick={() => onParticipate(challenge)}
              size="lg"
              className="flex-1"
              iconName="Heart"
              iconPosition="left"
            >
              {challenge?.isParticipating ? 'Donate More' : 'Join Challenge'}
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate(`/giving-challenges/${challenge?.id}`)}
              iconName="ExternalLink"
              iconPosition="right"
            >
              View Details
            </Button>
            <Button
              variant="ghost"
              size="lg"
              onClick={() => {
                navigator.share({
                  title: challenge?.title,
                  text: challenge?.description,
                  url: window.location?.href
                })?.catch(() => {
                  navigator.clipboard?.writeText(window.location?.href);
                });
              }}
              iconName="Share2"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturedChallenge;